import React, { useState, useEffect, Fragment } from "react";
import { Container, IconButton, ListItem } from "@mui/material";
import { Card, CardContent, CircularProgress } from "@mui/material";
import { Button, Typography, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import DialogBoxComponent from "../../../components/DialogBoxComponent";
import CSDataTable from "../../../components/CSDataTable";
import { CSDataTableColumns } from "../../../modal/CSDataTable";
import { IVulnerability } from "./modal";
import { useUserContext } from "../../../context/auth/UserContext";
import {
  deleteIVulnerablitiesMultiple,
  deleteVulnerability,
  fetchVulnerabilityData,
  getExistingVenurability,
} from "./helper";
import { VulnerabilityForm } from "./VulnerabilityForm";
import { exportExcel } from "./utils";
import { VulnerabilityImport } from "./VulnerabilityImport";
import { getExistingThreat } from "../ThreatSource/helper";
import { useRiskAssessmentContext } from "../../../context/RiskAssessment/RiskAssessmentContext";
import { useAccessRights } from "../../../utils/useAccessRights";

export default function RiskAssesmentVulnerability() {
  const navigate = useNavigate();

  const riskAssesment_CS_Access = useAccessRights("Risk Assessment - CS");

  const columns: CSDataTableColumns[] = [
    {
      accessorKey: "vulnerabiliteName",
      header: "Name",
      size: "70px",
    },
    {
      accessorKey: "edit",
      header: "",
      size: "30px",
      isEditable: riskAssesment_CS_Access?.isUpdate,
    },
    {
      accessorKey: "delete",
      header: "",
      size: "30px",
      isDeletable: riskAssesment_CS_Access?.isDelete,
    },
  ];
  const { setVulnerabilityList, vulnerabilityList } =
    useRiskAssessmentContext();

  const [isLoading, setIsLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState<IVulnerability | null>(null);
  const [openVulnerabilityForm, setOpenVulnerabilityForm] = useState(false);
  const [openVulnerabilityImport, setOpenVulnerabilityImport] = useState(false);
  const [multiSelectedRow, setMultiSelectedRow] = React.useState<IVulnerability[]>([]);

  const [dialogState, setDialogState] = useState({
    title: "",
    body: "",
    action: "",
    actionCancel: "",
    openDeleteConfirmDialog: false,
    openMultipleDeleteConfirmationDialog: false,
    openSuccessDialogBox: false,
  });

  const { userId, companyProfileID } = useUserContext();

  const handleSelectRow = (selectedRows: IVulnerability[]) => {
    setMultiSelectedRow(selectedRows);
  };
  const handleFormDialogClose = () => {
    setOpenVulnerabilityForm(false);
  };

  const handleAdd = () => {
    setSelectedRow(null);
    setOpenVulnerabilityForm(true);
  };

  const handleEdit = (row: IVulnerability) => {
    setSelectedRow(row);
    setOpenVulnerabilityForm(true);
  };

  const handleCloseDialog = () => {
    setDialogState({
      ...dialogState,
      openDeleteConfirmDialog: false,
      openMultipleDeleteConfirmationDialog: false,
      openSuccessDialogBox: false,
    });
  };
  const confirmDeleteAll = async () => {
    const vulnerabilityIds = multiSelectedRow.map(item => item.vulnerabilityId)
    try {
      const res = await getExistingVenurability(companyProfileID, vulnerabilityIds);
      if (res) {
        setDialogState({
          title: "Info",
          body: `Few vulnerabilities are assigned to Risk Assessments. Please un-assign from Risk Assessment list and then try to delete.`,
          action: "OK",
          actionCancel: "",
          openDeleteConfirmDialog: false,
          openMultipleDeleteConfirmationDialog: false,
          openSuccessDialogBox: true,
        });
      } else {
        setDialogState({
          title: "Confirmation",
          body: `Are you sure you want to delete the selected vulnerabilities`,
          action: "Delete",
          actionCancel: "Cancel",
          openDeleteConfirmDialog: false,
          openMultipleDeleteConfirmationDialog: true,
          openSuccessDialogBox: false,
        });
      }
    } finally {
      setIsLoading(false)
    }
  }
  const handleDelete = async (row: any) => {
    setIsLoading(true)
    setSelectedRow(row);
    try {
      const res = await getExistingVenurability(
        companyProfileID,
        [row.vulnerabilityId]
      );
      if (res) {
        setDialogState({
          title: "Info",
          body: `<strong>${row.vulnerabiliteName}</strong> cannot be removed as it is assigned to Risk Assessments. Please un-assign from Risk Assessments plan and then remove.`,
          action: "OK",
          actionCancel: "",
          openDeleteConfirmDialog: false,
          openMultipleDeleteConfirmationDialog: false,
          openSuccessDialogBox: true,
        });
      } else {
        setDialogState({
          title: "Confirmation",
          body: `Are you sure you want to delete <strong>${row.vulnerabiliteName}</strong>?`,
          action: "Delete",
          actionCancel: "Cancel",
          openDeleteConfirmDialog: true,
          openMultipleDeleteConfirmationDialog: false,
          openSuccessDialogBox: false,
        });
      }
    } finally {
      setIsLoading(false)
    }
  };
  const handleMultipleDeleteConfirm = async () => {
    handleCloseDialog()
    setIsLoading(true)
    const payload = multiSelectedRow.map(item => item.vulnerabilityId)
    const res = await deleteIVulnerablitiesMultiple(userId, payload)
    if (res.succeeded) {
      setDialogState({
        title: "Success",
        body: `Vulnerabilities has been deleted successfully `,
        action: "OK",
        actionCancel: "",
        openDeleteConfirmDialog: false,
        openMultipleDeleteConfirmationDialog: false,
        openSuccessDialogBox: true,
      });
      fetchVulnerability();
      setMultiSelectedRow([])
    } else {
      setDialogState({
        title: "Error",
        body: "Unexpected Error",
        action: "OK",
        actionCancel: "",
        openDeleteConfirmDialog: false,
        openMultipleDeleteConfirmationDialog: false,
        openSuccessDialogBox: true,
      });
    }
  }
  const handleDeleteVulnerability = async () => {
    handleCloseDialog()
    setIsLoading(true)
    const res = await deleteVulnerability(userId, selectedRow?.vulnerabilityId);
    if (res.succeeded === true) {
      setDialogState({
        title: "Success",
        body: `<strong>${selectedRow?.vulnerabiliteName}</strong> successfully deleted`,
        action: "OK",
        actionCancel: "",
        openDeleteConfirmDialog: false,
        openMultipleDeleteConfirmationDialog: false,
        openSuccessDialogBox: true,
      });
      fetchVulnerability()
    } else {
      setDialogState({
        title: "Error",
        body: res.error,
        action: "OK",
        actionCancel: "",
        openDeleteConfirmDialog: false,
        openMultipleDeleteConfirmationDialog: false,
        openSuccessDialogBox: true,
      });
    }
  };

  const displayImportVulnerability = () => {
    setOpenVulnerabilityImport(true);
  };

  const handleVulnerabilityImportCancel = () => {
    setOpenVulnerabilityImport(false);
  };

  const fetchVulnerability = async () => {
    setIsLoading(true);
    const res = await fetchVulnerabilityData(companyProfileID);
    if (res) {
      const formatList = res.map((ele: IVulnerability) => ({
        ...ele,
        rowId: ele.vulnerabilityId,
      })).sort((a: { vulnerabiliteName: string; }, b: { vulnerabiliteName: string; }) => {
        const nameA = a.vulnerabiliteName || '';
        const nameB = b.vulnerabiliteName || '';
        return nameA.localeCompare(nameB);
      })
      setVulnerabilityList(formatList);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchVulnerability();
  }, []);

  return (
    <Fragment>
      <Container sx={{ p: 1, height: "100%" }}>
        <Card sx={{ borderRadius: "10px", overflow: "hidden", height: "100%" }}>
          <CardContent>
            <Stack
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
            >
              <Stack alignItems="center" direction="row">
                <IconButton onClick={() => navigate("/risk-assessment")}>
                  <KeyboardBackspaceIcon />
                </IconButton>
                <Typography>Vulnerability</Typography>
              </Stack>

              <Stack alignItems="center" direction="row" spacing={2}>
                <Button
                  size="small"
                  variant="contained"
                  className="table-btn"
                  disabled={multiSelectedRow.length < 1}
                  onClick={() => confirmDeleteAll()}
                >
                  Delete
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  className="table-btn"
                  onClick={displayImportVulnerability}
                  disabled={!riskAssesment_CS_Access?.isImport || isLoading}
                >
                  Import
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  className="table-btn"
                  onClick={handleAdd}
                  disabled={!riskAssesment_CS_Access?.isCreate || isLoading}
                >
                  Create New Vulnerability
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  className="table-btn"
                  disabled={isLoading}
                  onClick={() => exportExcel(vulnerabilityList)}
                >
                  Download Spreadsheet
                </Button>
              </Stack>
            </Stack>
            <Container
              maxWidth="md"
              sx={{
                mt: 1,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {isLoading ? (
                <ListItem
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <CircularProgress />
                </ListItem>
              ) : (
                <CSDataTable
                  rows={vulnerabilityList}
                  columns={columns}
                  multiRowSelection={true}
                  handleEdit={handleEdit}
                  handleDelete={handleDelete}
                  handleSelectRow={(selectedRows) =>
                    handleSelectRow(selectedRows)
                  }
                />
              )}
            </Container>
          </CardContent>
        </Card>
      </Container>
      {
        <VulnerabilityForm
          openDialogBox={openVulnerabilityForm}
          list={vulnerabilityList}
          selectedRow={selectedRow}
          handleCancel={handleFormDialogClose}
          handleRefresh={fetchVulnerability}
        />
      }
      {
        <VulnerabilityImport
          openDialog={openVulnerabilityImport}
          vulnerabilityList={vulnerabilityList}
          handleCancel={handleVulnerabilityImportCancel}
          refreshList={() => fetchVulnerability()}
        />
      }

      {
        //Delete Multiple Confirm
        <DialogBoxComponent
          openDialogBox={dialogState.openMultipleDeleteConfirmationDialog}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogActionCancel={dialogState.actionCancel}
          dialogBody={dialogState.body}
          handleFunction={handleMultipleDeleteConfirm}
          handleCancel={handleCloseDialog}
        />
      }
      {
        //Delete Confirm
        <DialogBoxComponent
          openDialogBox={dialogState.openDeleteConfirmDialog}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogActionCancel={dialogState.actionCancel}
          dialogBody={dialogState.body}
          handleFunction={handleDeleteVulnerability}
          handleCancel={handleCloseDialog}
        />
      }
      {
        //Success
        <DialogBoxComponent
          openDialogBox={dialogState.openSuccessDialogBox}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogBody={dialogState.body}
          handleFunction={handleCloseDialog}
        />
      }
    </Fragment>
  );
}
